import { QuizQuestion } from '../types';

/**
 * Mock quiz questions for KBC - Kaun Banega CoCaptain
 * Theme: GenAI, Shipment CoCaptain, and AI Concepts
 * Organized by difficulty tier (1-5), with 6+ questions per tier
 */
export const MOCK_QUESTIONS: QuizQuestion[] = [
  // ===== TIER 1: Intro / Themed (Gemini / Basics) =====
  {
    id: 't1_q01',
    text: "To present its findings clearly in the ship's log for everyone on deck to read, Shipment CoCaptain uses a simple, structured language that's easy for both humans and machines to parse. What is this universal format called?",
    options: [
      { id: 'a', text: 'JSON Cargo Manifest' },
      { id: 'b', text: 'Binary Signal Flags' },
      { id: 'c', text: "The Captain's Markdown" },
      { id: 'd', text: "Plain Text Sailor's Scrawl" },
    ],
    correctOption: 'c',
    explanation: "The Captain's Markdown is a metaphor for clean, structured Markdown output—easy to parse by both humans and machines, and ideal for rendering formatted content in interfaces.",
    level: 1,
    tier: 1,
  },
  {
    id: 't1_q02',
    text: "When a sailor asks a question, CoCaptain first consults the latest ship manuals and cargo ledgers before replying. This approach of combining external knowledge with model responses is known as:",
    options: [
      { id: 'a', text: 'Model Fine-Tuning' },
      { id: 'b', text: 'Retrieval-Augmented Generation (RAG)' },
      { id: 'c', text: 'Context Expansion' },
      { id: 'd', text: 'Foundational Pre-Training' },
    ],
    correctOption: 'b',
    explanation: 'Retrieval-Augmented Generation (RAG) allows a model to use external, up-to-date knowledge sources without retraining—much like CoCaptain consulting the ship\'s ledgers before responding.',
    level: 1,
    tier: 1,
  },
  {
    id: 't2_q03',
    text: "The initial instructions given to Shipment CoCaptain at the start of a conversation, which define its personality and goals (e.g., 'You are a helpful shipping logistics assistant'), are known as the...",
    options: [
      { id: 'a', text: 'System Prompt' },
      { id: 'b', text: 'User Query' },
      { id: 'c', text: 'Fine-Tuning Data' },
      { id: 'd', text: 'Output Schema' },
    ],
    correctOption: 'a',
    explanation: 'The System Prompt sets the tone, role, and behavioral guidelines for the model at the start of every conversation — it defines how CoCaptain should respond throughout the interaction.',
    level: 2,
    tier: 2,
  },
  {
    id: 't1_q04',
    text: "The foundational technology that allows Shipment CoCaptain to understand the context and nuances of a user's request is called a...",
    options: [
      { id: 'a', text: 'Relational Database' },
      { id: 'b', text: 'Large Language Model (LLM)' },
      { id: 'c', text: 'Python Interpreter' },
      { id: 'd', text: 'Recommendation Engine' },
    ],
    correctOption: 'b',
    explanation: 'A Large Language Model (LLM) like GPT-4 provides the linguistic understanding that powers CoCaptain\'s ability to interpret and respond naturally.',
    level: 1,
    tier: 1,
  },
  {
    id: 't1_q05',
    text: "If CoCaptain produces a factually incorrect statement about a shipment's status, this error is commonly referred to as a(n)...",
    options: [
      { id: 'a', text: 'Hallucination' },
      { id: 'b', text: 'System Glitch' },
      { id: 'c', text: 'Compilation Error' },
      { id: 'd', text: 'Data Anomaly' },
    ],
    correctOption: 'a',
    explanation: 'In the context of LLMs, a "hallucination" occurs when the model generates inaccurate or made-up information, even if it sounds confident or plausible.',
    level: 1,
    tier: 1,
  },

  // ===== TIER 2: Practitioner / Tooling Basics =====
{
  id: 't2_q03',
  text: "The initial instructions given to Shipment CoCaptain at the start of a conversation, which define its personality and goals (e.g., 'You are a helpful shipping logistics assistant'), are known as the...",
  options: [
    { id: 'a', text: 'System Prompt' },
    { id: 'b', text: 'User Query' },
    { id: 'c', text: 'Fine-Tuning Data' },
    { id: 'd', text: 'Output Schema' },
  ],
  correctOption: 'a',
  explanation: 'The System Prompt sets the tone, role, and behavioral guidelines for the model at the start of every conversation — it defines how CoCaptain should respond throughout the interaction.',
  level: 2,
  tier: 2,
},
{
  id: 't1_q04',
  text: "The foundational technology that allows Shipment CoCaptain to understand the context and nuances of a user's request is called a...",
  options: [
    { id: 'a', text: 'Relational Database' },
    { id: 'b', text: 'Large Language Model (LLM)' },
    { id: 'c', text: 'Python Interpreter' },
    { id: 'd', text: 'Recommendation Engine' },
  ],
  correctOption: 'b',
  explanation: 'A Large Language Model (LLM) like GPT-4 provides the linguistic understanding that powers CoCaptain’s ability to interpret and respond naturally.',
  level: 1,
  tier: 1,
},
{
  id: 't1_q05',
  text: "If CoCaptain produces a factually incorrect statement about a shipment's status, this error is commonly referred to as a(n)...",
  options: [
    { id: 'a', text: 'Hallucination' },
    { id: 'b', text: 'System Glitch' },
    { id: 'c', text: 'Compilation Error' },
    { id: 'd', text: 'Data Anomaly' },
  ],
  correctOption: 'a',
  explanation: 'In the context of LLMs, a “hallucination” occurs when the model generates inaccurate or made-up information, even if it sounds confident or plausible.',
  level: 1,
  tier: 1,
},

  // ===== TIER 2: Practitioner / Tooling Basics =====
  {
    id: 't2_q01',
    text: 'A user asks CoCaptain about "demurrage charges," but CoCaptain retrieves documents about "late container fees" instead. This works because the two concepts vector embeddings are:',
    options: [
      { id: 'a', text: 'stored in the same hash bucket' },
      { id: 'b', text: 'tagged with identical metadata' },
      { id: 'c', text: 'lexically identical' },
      { id: 'd', text: 'mathematically close in vector space' },
    ],
    correctOption: 'd',
    explanation: 'In vector search, semantically similar concepts (like "demurrage" and "late fees") have embeddings that are close together in the high-dimensional space, even if their text differs.',
    level: 2,
    tier: 2,
  },
  {
    id: 't2_q02',
    text: 'An analyst asks Shipment CoCaptain to "summarize the top 5 loss making reasons". The CoCaptain receives gathered knowledge and data, and then generates a new textual summary in its own words. This is an example of:',
    options: [
      { id: 'a', text: 'Extractive Summarization' },
      { id: 'b', text: 'Abstractive Summarization' },
      { id: 'c', text: 'Response Regurgitation' },
      { id: 'd', text: 'Query-Focused Summarization' },
    ],
    correctOption: 'b',
    explanation: 'Abstractive methods create novel text to convey the source content\'s meaning, as opposed to extractive methods that pick existing sentences.',
    level: 2,
    tier: 2,
  },
  {
    id: 't2_q03',
    text: 'A database schema for shipments has dozens of tables, but a particular query only needs two of them. The developer removes all irrelevant tables/columns from the prompt to simplify CoCaptain\'s context. What is this practice called?',
    options: [
      { id: 'a', text: 'Table Reranking' },
      { id: 'b', text: 'Context Clearing' },
      { id: 'c', text: 'Schema Pruning' },
      { id: 'd', text: 'Index Optimization' },
    ],
    correctOption: 'c',
    explanation: 'Schema pruning trims the provided database schema to include only tables/columns relevant to the query, reducing context size and improving query generation accuracy.',
    level: 2,
    tier: 2,
  },
  {
    id: 't2_q04',
    text: 'To protect against prompt injection attacks where users try to manipulate CoCaptain into revealing database credentials, the system should implement:',
    options: [
      { id: 'a', text: 'Strong encryption protocols' },
      { id: 'b', text: 'Guardrails' },
      { id: 'c', text: 'Tool calling for credential verification' },
      { id: 'd', text: 'Multi-agent validation loops' },
    ],
    correctOption: 'b',
    explanation: 'These are safety mechanisms specifically designed to detect and block adversarial prompts and prevent harmful outputs. These should include manual verification practices which programmatically check LLM input/output correctness, rather than relying on other models alone for validation.',
    level: 2,
    tier: 2,
  },
  {
    id: 't2_q05',
    text: 'For evaluating the quality of retrieved documents in a RAG pipeline, which metric measures what percentage of the retrieved documents were actually relevant to answering the query?',
    options: [
      { id: 'a', text: 'Recall@K' },
      { id: 'b', text: 'Context Precision' },
      { id: 'c', text: 'Similarity Score' },
      { id: 'd', text: 'Answer Relevance' },
    ],
    correctOption: 'b',
    explanation: 'This RAG-specific metric evaluates the signal-to-noise ratio of retrieved documents, measuring how many were useful versus irrelevant.',
    level: 2,
    tier: 2,
  },
  {
    id: 't2_q06',
    text: 'Say we wish to teach Shipment CoCaptain a new, distinct conversational style for interacting with shipping logistics managers, making it more concise and using specific industry jargon. This change needs to be a core, stylistic modification of the model\'s behavior. Which method is most appropriate for instilling such a style into the model?',
    options: [
      { id: 'a', text: 'Adding cues it to the system prompt' },
      { id: 'b', text: 'Storing style examples in a RAG vector database' },
      { id: 'c', text: 'Fine-tuning the model on training examples' },
      { id: 'd', text: 'Using few-shot examples in every query' },
    ],
    correctOption: 'c',
    explanation: 'This retrains the model\'s weights to internalize new knowledge, unlike RAG or prompting which provide external context, making the new behaviour a fundamental part of how the model generates responses.',
    level: 2,
    tier: 2,
  },
  {
    id: 't2_q07',
    text: 'CoCaptain is configured to output JSON conforming to a strict schema (e.g. for ensuring the parts of the initial plan are correctly parsed). To guarantee valid syntax, the system employs a technique known as:',
    options: [
      { id: 'a', text: 'Structured Output Generation' },
      { id: 'b', text: 'Schema-Guided Parsing' },
      { id: 'c', text: 'Deterministic Generation' },
      { id: 'd', text: 'Output Sanitization' },
    ],
    correctOption: 'a',
    explanation: 'By using "structured output" mode, the LLM is guided to produce exactly valid JSON per a schema, ensuring no format errors',
    level: 2,
    tier: 2,
  },
  {
    id: 't2_q08',
    text: 'To ensure Shipment CoCaptain provides accurate citations when answering "What caused the Q3 Asia-Pacific delays?", the system must link each claim back to source documents. This practice is called:',
    options: [
      { id: 'a', text: 'Source Retrieval' },
      { id: 'b', text: 'Answer Grounding' },
      { id: 'c', text: 'Reference Mapping' },
      { id: 'd', text: 'Provenance Tracking' },
    ],
    correctOption: 'b',
    explanation: 'In RAG systems, grounding specifically refers to anchoring LLM-generated responses to retrieved source documents, ensuring verifiable and traceable claims.',
    level: 2,
    tier: 2,
  },

  // ===== TIER 3: Advanced Practitioner =====
  {
    id: 't3_q01',
    text: 'When an agent\'s generated SQL query fails with an error, it analyzes the error message and rewrites the query. This self-correction loop continues until success or max attempts. Which component architecture best supports this pattern?',
    options: [
      { id: 'a', text: 'Sequential chain with error callbacks' },
      { id: 'b', text: 'Graph-based execution with conditional edges' },
      { id: 'c', text: 'Multi-agent debate system' },
      { id: 'd', text: 'Hierarchical workflow with supervisors' },
    ],
    correctOption: 'b',
    explanation: 'Graph architectures (like LangGraph) naturally support loops and conditional branching based on execution outcomes, ideal for retry logic.',
    level: 3,
    tier: 3,
  },
  {
    id: 't3_q02',
    text: 'For a large shipping database with 800 columns across 50 tables, providing the full schema to the LLM causes context overflow and poor SQL generation. Which optimization technique specifically addresses this?',
    options: [
      { id: 'a', text: 'Using approximate nearest neighbor search for schema matching' },
      { id: 'b', text: 'Schema pruning based on query intent' },
      { id: 'c', text: 'Implementing column-level access controls' },
      { id: 'd', text: 'Caching frequently accessed table definitions' },
    ],
    correctOption: 'b',
    explanation: 'This technique dynamically filters the schema to include only relevant tables/columns based on the query, reducing context size and improving generation accuracy.',
    level: 3,
    tier: 3,
  },
  {
    id: 't3_q03',
    text: 'An "Auditor" agent working with CoCaptain reviews the SQL queries of a "Data" agent and flags any errors. What pattern is this?',
    options: [
      { id: 'a', text: 'Self-Correction Loop' },
      { id: 'b', text: 'Chain-of-Thought Reasoning' },
      { id: 'c', text: 'Multi-Agent Debate' },
      { id: 'd', text: 'LLM-as-a-Judge' },
    ],
    correctOption: 'd',
    explanation: 'This design, where one agent (an LLM) formally evaluates another\'s output, is known as LLM-as-Judge: one model is acting as a "judge" to validate the other\'s work.',
    level: 3,
    tier: 3,
  },
  {
    id: 't3_q04',
    text: 'When evaluating quality of word or sentence embeddings, which of the following is an example of intrinsic evaluation?',
    options: [
      { id: 'a', text: 'Measuring the F1-score of a downstream text classification task that uses the embeddings.' },
      { id: 'b', text: 'Measuring semantic clustering tightness using a metric like Silhouette score' },
      { id: 'c', text: 'Calculating the hit rate of a RAG system that uses the embeddings for retrieval.' },
      { id: 'd', text: 'Checking for the improvement in a recommendation system\'s click-through rate after integrating the embeddings.' },
    ],
    correctOption: 'b',
    explanation: 'Intrinsic evaluations assess the inherent qualities of the embeddings themselves, such as their ability to capture semantic relationships, independent of any specific downstream application. The other options are all examples of extrinsic evaluation',
    level: 3,
    tier: 3,
  },
  {
    id: 't3_q05',
    text: 'When evaluating a hypothetical RAG system, the team observes: Context Recall = 72%, Context Precision = 91%, Answer Faithfulness = 88%, Answer Relevance = 85%. A business analyst complains: "The system gives accurate information, but misses important details from the knowledge base." Which metric directly reflects this specific problem?',
    options: [
      { id: 'a', text: 'Low Context Precision' },
      { id: 'b', text: 'Low Context Recall' },
      { id: 'c', text: 'Low Answer Faithfulness' },
      { id: 'd', text: 'Low Answer Relevance' },
    ],
    correctOption: 'b',
    explanation: 'At 72%, the retrieval system fails to fetch ~28% of relevant documents from the knowledge base, explaining why important details are missed. The other metrics are relatively strong, confirming this is a retrieval coverage issue, not a generation problem.',
    level: 3,
    tier: 3,
  },
  {
    id: 't3_q06',
    text: 'If the CoCaptain were to compare the response it comes up with against existing gold standard answers, which metric focuses on semantic similarity between generated and reference answers, rather than exact lexical overlap?',
    options: [
      { id: 'a', text: 'BLEU' },
      { id: 'b', text: 'BERTScore' },
      { id: 'c', text: 'ROUGE-L' },
      { id: 'd', text: 'F1 Score' },
    ],
    correctOption: 'b',
    explanation: 'BLEU/ROUGE are surface-level, while BERTScore compares contextual embeddings for semantic similarity.',
    level: 3,
    tier: 3,
  },

  // ===== TIER 4: Senior / Architect =====
  {
    id: 't4_q01',
    text: 'A multi-agent workflow similar to Shipment CoCaptain\'s includes a SQL agent, analysis agent, visualization agent, and validator agent. A failure occurs because the analysis agent expects fully cleaned data, but the SQL agent sometimes returns null values. What\'s the most robust architectural fix?',
    options: [
      { id: 'a', text: 'Combine the SQL and analysis logic into a single agent to eliminate the interface and ensure data consistency.' },
      { id: 'b', text: 'Add a data validation and sanitization tool to the analysis agent, making it more resilient to upstream data quality issues' },
      { id: 'c', text: 'Implement a graph-based structure with conditional routing that validates the SQL agent\'s output and can trigger a retry or a cleaning step before passing data to the analysis agent.' },
      { id: 'd', text: 'Improve the SQL agent\'s prompt with more detailed few-shot examples of correctly formatted, null-free data to reduce the error rate at the source.' },
    ],
    correctOption: 'c',
    explanation: 'While other options offer partial solutions, a graph-based architecture with conditional routing is the most robust fix. This is more architecturally sound than making each agent responsible for cleaning its own inputs (B), relying on prompting alone which isn\'t guaranteed (D), or sacrificing modularity by merging agents (A).',
    level: 4,
    tier: 4,
  },
  {
    id: 't4_q02',
    text: 'A 500-page technical manual is chunked into sections semantically by topics. retrieval performance suffers for queries whose answers lie at the boundary between two semantic chunks. Which chunking strategy most effectively mitigates this "lost-in-the-middle" problem without sacrificing the benefits of semantic coherence?',
    options: [
      { id: 'a', text: 'Decrease the chunk size to create more granular units, increasing the chance of a single chunk containing the full answer.' },
      { id: 'b', text: 'Re-process the document using a sentence-window approach, where each sentence becomes the core of a chunk expanded by its neighboring sentences.' },
      { id: 'c', text: 'Implement a chunk overlap strategy, where a small portion of the end of each chunk is repeated at the beginning of the subsequent chunk.' },
      { id: 'd', text: 'Generate summary embeddings for each semantic chunk to provide the retriever with a higher-level understanding of the content.' },
    ],
    correctOption: 'c',
    explanation: 'Chunk overlap is the standard and most direct solution to this boundary problem. By including a small amount of context from the previous chunk, it ensures that concepts spanning two chunks are fully contained within at least one of the chunks, making them retrievable. While other options are valid chunking strategies, they don\'t as directly address the specific issue of semantic boundaries.',
    level: 4,
    tier: 4,
  },
  {
    id: 't4_q03',
    text: 'A new agent being developed for Shipment CoCaptain is designed to produce a JSON object summarizing a shipping incident. The output must adhere to a strict schema and make a valid choice from a predefined list of incident categories (e.g., "Mechanical," "Weather," "Human Error"). Which technique is most effective at guaranteeing both syntactic validity and semantic correctness?',
    options: [
      { id: 'a', text: 'Post-processing validation that checks the generated JSON against the schema and the category list, requesting a regeneration on failure.' },
      { id: 'b', text: 'Using a model fine-tuned on thousands of examples of valid JSON outputs for this specific task' },
      { id: 'c', text: 'Employing few-shot prompting with examples that demonstrate correct category choices within the JSON structure.' },
      { id: 'd', text: 'Using constrained generation where the JSON schema and the enumerated list of valid categories are enforced at the token level during decoding.' },
    ],
    correctOption: 'd',
    explanation: 'Constrained generation is designed for this exact purpose. It guarantees that the output adheres to both the structural rules of the JSON schema and the semantic constraints (like an enum list) by restricting the model\'s vocabulary at each step of the generation process.',
    level: 4,
    tier: 4,
  },
  {
    id: 't4_q04',
    text: 'If Shipment CoCaptain\'s retriever agent successfully finds relevant documents for nearly every query and ranks them highly - but the final answers are verbose and often contain tangential, distracting information, leading to low user satisfaction. Which evaluation blind spot caused this issue?',
    options: [
      { id: 'a', text: 'Overreliance on recall, ignoring context noise' },
      { id: 'b', text: 'Overemphasis on semantic similarity' },
      { id: 'c', text: 'Overly focusing on lexical over semantic meaning' },
      { id: 'd', text: 'Hybrid retrieval misconfiguration' },
    ],
    correctOption: 'a',
    explanation: 'Overreliance on recall — ignoring context noise. That is, focusing metrics solely on the retrieval of relevant items, while neglecting the impact of also retrieving irrelevant ones.',
    level: 4,
    tier: 4,
  },
  {
    id: 't4_q05',
    text: 'To improve the analytical depth of CoCaptain on a given complex query about shipments - without using external databases or invoking too many agents - which prompting strategy encourages the model to internally break down its reasoning into logical steps before generating the final answer?',
    options: [
      { id: 'a', text: 'Tool-Use prompting' },
      { id: 'b', text: 'Dynamic Routing' },
      { id: 'c', text: 'Async Batch Processing' },
      { id: 'd', text: 'Chain-of-Thought' },
    ],
    correctOption: 'd',
    explanation: 'Chain-of-Thought prompting explicitly asks the model to output intermediate reasoning steps before arriving at a conclusion, deepening the logical rigor of its answers—all within a single agent and without invoking external APIs or parallel processing.',
    level: 4,
    tier: 4,
  },

  // ===== TIER 5: Architect / Expert =====
  {
    id: 't5_q01',
    text: 'During a RAG system evaluation, the team observes: The retriever often includes relevant docs but ranks the critical ones poorly. Final answers still hallucinate because low-ranked documents are rarely seen. Which metric best surfaces this issue?',
    options: [
      { id: 'a', text: 'Context Precision' },
      { id: 'b', text: 'Recall@k' },
      { id: 'c', text: 'NDCG' },
      { id: 'd', text: 'BLEU' },
    ],
    correctOption: 'c',
    explanation: 'Normalized Discounted Cumulative Gain (NDCG) is a rank-aware metric that evaluates the quality of a ranked list. It assigns a higher score for relevant documents ranked earlier and heavily discounts the value of relevant documents that appear lower in the list, directly surfacing the described issue.',
    level: 5,
    tier: 5,
  },
  {
    id: 't5_q02',
    text: 'Shipment CoCaptain needs to calculate the total cost and risk for a new inland corridor. This requires sequential, conditional steps (e.g., Retrieve Data → Analyze Contextual Risk → Synthesize Report). The plan must adapt dynamically if data retrieval fails or returns insufficient information, potentially triggering a re-query. Which agentic pattern is required to dynamically generate and coordinate this multi-step plan and delegate tasks to specialized subagents?',
    options: [
      { id: 'a', text: 'Tool Use Pattern' },
      { id: 'b', text: 'Routing Workflow Pattern' },
      { id: 'c', text: 'Prompt Chaining Pattern' },
      { id: 'd', text: 'Orchestrator-Worker Pattern' },
    ],
    correctOption: 'd',
    explanation: 'This planning pattern is essential for complex, non-deterministic tasks. It involves an "orchestrator" LLM that creates a plan, dispatches tasks to "worker" agents or tools, and dynamically adapts the plan based on the results, handling failures and unexpected outcomes.',
    level: 5,
    tier: 5,
  },
  {
    id: 't5_q03',
    text: 'A "Report Generation" agent in CoCaptain produces complex JSON reports. The system mandate requires the agent to autonomously check its output against a compliance checklist and revise the report iteratively until all mandatory fields are correctly populated. Which agentic pattern is the most useful for enabling this iterative self-verification cycle based on LLM-generated critique?',
    options: [
      { id: 'a', text: 'Multi-Agent Pattern (Agentic Debating)' },
      { id: 'b', text: 'Multi-Worker Parallelization' },
      { id: 'c', text: 'Tool Use (Function Calling)' },
      { id: 'd', text: 'Reflection Pattern (Evaluator-Optimizer)' },
    ],
    correctOption: 'd',
    explanation: 'The Reflection Pattern allows an agent to evaluate its own output and iteratively improve it based on self-critique, making it ideal for quality assurance workflows.',
    level: 5,
    tier: 5,
  },
  {
    id: 't5_q04',
    text: 'In a multi-agent research system, what is the primary architectural advantage of deploying multiple subagents to work in parallel on different facets of a query?',
    options: [
      { id: 'a', text: 'It minimizes overall token consumption by sharing a unified context window.' },
      { id: 'b', text: 'It enables comprehensive, breadth-first exploration of a topic by parallelizing information retrieval.' },
      { id: 'c', text: 'It guarantees higher factual accuracy by allowing subagents to vote on the correct answer.' },
      { id: 'd', text: 'It simplifies the system\'s architecture by removing the need for a central orchestrator agent.' },
    ],
    correctOption: 'b',
    explanation: 'The core benefit of this architecture for research is its ability to mimic how humans explore complex topics—by pursuing multiple independent lines of inquiry at once. This "breadth-first" approach reduces the risk of path dependency where an early, potentially incorrect, finding biases the entire research direction.',
    level: 5,
    tier: 5,
  },
  {
    id: 't5_q05',
    text: 'To analyze complex maritime risk, CoCaptain needs to retrieve structured relationships (e.g., Vessel X is owned by Company Y which is restricted in Trade Lane Z) alongside general semantic information. Which retrieval enhancement specifically targets finding these highly structural, relational facts that standard vector similarity search often misses?',
    options: [
      { id: 'a', text: 'Multi-hop retrieval using a secondary vector index' },
      { id: 'b', text: 'Implementing hybrid (sparse + dense) retrieval' },
      { id: 'c', text: 'GraphRAG and knowledge graph retrieval techniques' },
      { id: 'd', text: 'Applying small overlapping sliding windows to semantic chunks' },
    ],
    correctOption: 'c',
    explanation: 'GraphRAG and knowledge graph retrieval techniques are specifically designed to capture and traverse entity relationships, making them ideal for queries requiring multi-hop reasoning over structured facts.',
    level: 5,
    tier: 5,
  },
  {
    id: 't5_q06',
    text: 'Shipment CoCaptain needs to execute a complex analysis that requires 10 concurrent summarization tasks on independent shipping documents to meet a strict low-latency requirement. The results from all 10 tasks must then be combined by a final aggregator LLM. Which workflow pattern is specifically designed to minimize wall-clock execution time by executing these independent subtasks simultaneously?',
    options: [
      { id: 'a', text: 'Prompt Chaining' },
      { id: 'b', text: 'Routing or Handoff Workflow' },
      { id: 'c', text: 'Reflection Pattern' },
      { id: 'd', text: 'Parallelization Workflow' },
    ],
    correctOption: 'd',
    explanation: 'Parallelization Workflow is designed to execute independent tasks concurrently, reducing total execution time when tasks can be processed simultaneously.',
    level: 5,
    tier: 5,
  },
  {
    id: 't5_q07',
    text: 'If CoCaptain needs to process shipment manifests through three fixed steps — extraction, validation, and summarization — with minimal latency but no dynamic planning. Which architecture is best for this workflow?',
    options: [
      { id: 'a', text: 'Plan and Correct' },
      { id: 'b', text: 'Reflection Pattern' },
      { id: 'c', text: 'Prompt Chaining' },
      { id: 'd', text: 'Multi-Agent System' },
    ],
    correctOption: 'c',
    explanation: 'Prompt Chaining Workflow is ideal for fixed sequential steps where each step\'s output feeds directly into the next without requiring dynamic replanning or complex coordination.',
    level: 5,
    tier: 5,
  },
];

/**
 * Get a question by its ID
 */
export const getQuestionById = (id: string): QuizQuestion | undefined => {
  return MOCK_QUESTIONS.find(q => q.id === id);
};

/**
 * Get questions for a specific tier
 */
export const getQuestionsByTier = (tier: number): QuizQuestion[] => {
  return MOCK_QUESTIONS.filter(q => q.tier === tier || q.level === tier);
};

/**
 * Shuffle array (Fisher-Yates algorithm)
 * Ensures true randomization with cryptographically secure random values
 */
const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Get questions for a quiz session with difficulty tiers
 * 
 * RANDOMIZATION STRATEGY (Per-Tier):
 * 1. For each tier (1-5), get all available questions in that tier's pool
 * 2. Shuffle the entire pool using Fisher-Yates algorithm (true randomization)
 * 3. Select the first N questions from the shuffled pool (N = questionsPerTier)
 * 4. This ensures each quiz attempt has a unique random subset per tier
 * 
 * PRESENTATION ORDER:
 * - Questions are presented in tier order: Tier 1 → Tier 2 → Tier 3 → Tier 4 → Tier 5
 * - Within each tier, the selected questions maintain their shuffled order
 * - This creates a difficulty progression while ensuring variety
 * 
 * @param questionsPerTier - Number of questions to select from each tier (default: 4)
 * @param tierCount - Number of difficulty tiers (default: 5)
 * @returns Array of randomly selected questions, ordered by tier difficulty
 * 
 * @example
 * // Get 4 random questions from each of 5 tiers (20 total questions)
 * const quizQuestions = getQuizQuestions(4, 5);
 * 
 * // Get 3 random questions from each of 3 tiers (9 total questions)
 * const shortQuiz = getQuizQuestions(3, 3);
 */
export const getQuizQuestions = (questionsPerTier: number = 4, tierCount: number = 5): QuizQuestion[] => {
  const selectedQuestions: QuizQuestion[] = [];
  
  // Select random questions from each tier
  for (let tier = 1; tier <= tierCount; tier++) {
    const tierQuestions = getQuestionsByTier(tier);
    
    // Shuffle the entire tier pool for true randomization
    const shuffled = shuffleArray(tierQuestions);
    
    // Select the first N questions from the shuffled pool
    const selected = shuffled.slice(0, questionsPerTier);
    
    // Add tier questions in shuffled order to maintain variety
    selectedQuestions.push(...selected);
  }
  
  return selectedQuestions;
};
